import classNames from 'classnames';
import React from 'react';
import InfoIcon from 'assets/infoIcon.svg';
import AlertTriangleIcon from 'assets/alertTriangle.svg';
import CheckIcon from 'assets/check.svg';
import CloseIcon from 'assets/close.svg';
import ThumbsUpIcon from 'assets/thumbsUpIcon.svg';
import ThumbsUpActiveIcon from 'assets/thumbsUpActiveIcon.svg';
import ThumbsDownIcon from 'assets/thumbsDownIcon.svg';
import ThumbsDownActiveIcon from 'assets/thumbsDownActiveIcon.svg';
import { Controls } from 'components/Controls';
import styles from './Notification.module.scss';
import { Notification as NotificationType } from '@yleisradio/areena-types';

export type NotificationStyle =
  'transparent' | 'primary' | 'text' | 'error' | undefined;

export type NotificationIcon =
  | 'info'
  | 'error'
  | 'success'
  | 'none'
  | 'thumbs-up'
  | 'thumbs-up-active'
  | 'thumbs-down'
  | 'thumbs-down-active';

interface Props {
  notification: NotificationType;
  notificationStyle: NotificationStyle;
  notificationIcon?: NotificationIcon | undefined;
  onClose?: () => void;
  helpText?: string | undefined;
  isToast?: boolean;
}

type IconProps = {
  notificationIcon: NotificationIcon;
  className: string | undefined;
  'aria-hidden': boolean;
};

const Icon = ({ notificationIcon, ...props }: IconProps) => {
  switch (notificationIcon) {
    case 'none':
      return null;
    case 'error':
      return <AlertTriangleIcon {...props} />;
    case 'success':
      return <CheckIcon {...props} />;
    case 'thumbs-up':
      return <ThumbsUpIcon {...props} />;
    case 'thumbs-up-active':
      return <ThumbsUpActiveIcon {...props} />;
    case 'thumbs-down':
      return <ThumbsDownIcon {...props} />;
    case 'thumbs-down-active':
      return <ThumbsDownActiveIcon {...props} />;
    default:
      return <InfoIcon {...props} />;
  }
};

function getDefaultIcon(
  notificationStyle: NotificationStyle
): NotificationIcon {
  switch (notificationStyle) {
    case 'text':
      return 'none';
    case 'error':
      return 'error';
    default:
      return 'info';
  }
}

type NotificationTextProps = {
  notification: string | undefined;
  helpText: string | undefined;
};

const NotificationText = ({
  notification,
  helpText,
}: NotificationTextProps) => {
  if (helpText) {
    return (
      <span className={styles.text}>
        {notification}
        <span className={styles.helpText}>{helpText}</span>
      </span>
    );
  }

  return <>{notification}</>;
};

export const Notification: React.FunctionComponent<Props> = ({
  notification,
  notificationStyle = 'transparent',
  notificationIcon,
  onClose,
  helpText,
  isToast = false,
}) => {
  const className = classNames(
    styles.notification,
    styles[notificationStyle],
    isToast && styles.toast
  );
  const role = isToast ? 'alert' : undefined;

  return (
    <span className={className} role={role} data-testid="notification">
      <Icon
        aria-hidden
        className={styles.icon}
        notificationIcon={notificationIcon ?? getDefaultIcon(notificationStyle)}
      />
      <NotificationText
        notification={notification?.uiMessage}
        helpText={helpText}
      />
      {onClose && (
        <button onClick={onClose} className={styles.removeButton}>
          <CloseIcon className={styles.icon} />
        </button>
      )}
      {notification.controls && notification.controls.length > 0 && (
        <div className={styles.controlWrapper}>
          <Controls
            controls={notification.controls}
            as="button"
            size={'sm'}
            variation={'primary'}
            direction={'horizontal'}
          />
        </div>
      )}
    </span>
  );
};
